import { matchPath, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import Iconify from 'src/components/iconify';

import { useMergeRun } from '../context/merge-run-context';

// The floating panel that shows the merge-duplicates run in progress, wherever the seller is in
// the app. It only reads the run — starting a run and retrying a failed group live on the setup
// and batch pages, not here.

const STATUS_DISPLAY = {
  queued: { icon: 'solar:clock-circle-bold', color: 'text.secondary', text: 'Starting…' },
  processing: { icon: 'solar:refresh-bold', color: 'info.main', text: 'Merging…' },
  completed: { icon: 'solar:check-circle-bold', color: 'success.main', text: 'Done' },
  completed_with_errors: {
    icon: 'solar:danger-triangle-bold',
    color: 'warning.main',
    text: 'Done, some couldn’t be merged',
  },
  expired: {
    icon: 'solar:danger-triangle-bold',
    color: 'text.secondary',
    text: 'This run is no longer available',
  },
  not_found: {
    icon: 'solar:danger-triangle-bold',
    color: 'text.secondary',
    text: 'This run is no longer available',
  },
};

function headerColor(overallStatus) {
  if (overallStatus === 'completed') return 'success.lighter';
  if (overallStatus === 'completed_with_errors') return 'warning.lighter';
  if (overallStatus === 'expired' || overallStatus === 'not_found') return 'background.neutral';
  return 'info.lighter';
}

export default function MergeWidget() {
  const { activeRun, serverStatus, counts, overallStatus, dismissRun } = useMergeRun();

  const router = useRouter();
  const { pathname } = useLocation();

  if (!activeRun) return null;

  // The run's own page shows everything this panel does and more, so the panel keeps out of its
  // way while the seller is already looking at it.
  const batchPage = matchPath('/listing/merge/batch/:batchId', pathname);
  if (batchPage?.params?.batchId === activeRun.batchId) return null;

  const status = STATUS_DISPLAY[overallStatus] || STATUS_DISPLAY.queued;

  // The run's total is a group count, and stays at the estimate the seller submitted until the
  // first poll answers with the server's own — the two only ever disagree in that first moment.
  const total = serverStatus?.total || activeRun.groupCount || 0;
  const settled = counts.succeeded + counts.failed;
  const progressValue = total > 0 ? Math.min(100, Math.round((settled / total) * 100)) : 0;

  const handleViewDetails = () => {
    router.push(paths.dashboard.listing.mergeBatch(activeRun.batchId));
  };

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1300,
          borderRadius: 2,
          overflow: 'hidden',
          width: 340,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: headerColor(overallStatus),
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Iconify icon={status.icon} width={24} sx={{ color: status.color }} />
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Merging duplicates
          </Typography>
          <IconButton size="small" onClick={dismissRun}>
            <Iconify icon="eva:close-outline" width={18} />
          </IconButton>
        </Box>

        <LinearProgress variant="determinate" value={progressValue} sx={{ height: 4 }} />

        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {status.text}
            </Typography>

            <Typography variant="body2" fontWeight={600}>
              {counts.succeeded} merged
              {counts.failed > 0 && ` · ${counts.failed} need attention`}
            </Typography>

            <Button fullWidth variant="outlined" onClick={handleViewDetails}>
              View details
            </Button>
          </Stack>
        </CardContent>
      </Paper>
    </Slide>
  );
}
